import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiLoader } from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { LoaderType } from "@miplatform/types";
import { Box, Typography } from "@mui/material";
import {
  type StagedWorkOrderAssignment,
  getExpectedDurationMinutes,
} from "../NHCPriorityScheduler/useAssignmentStaging";
import { IAssignableUser } from "../NhcCalendar.types";
import { AssignableUserCard } from "./AssignableUserCard";

export const AssignableUserContainer = ({
  assignableUsersAvailableTime,
  isLoading,
  selectedUserId,
  activeAssignableUserIds = [],
  stagedByUserId = {},
  onUserSelect,
  onAddAssignableUser,
}: AssignableUserContainerProps) => {
  const { classes, theme } = useStyles();

  const { stagedDurationHoursByUserId, stagedCountByUserId } = useMemo(() => {
    const hours: Record<string, number> = {};
    const counts: Record<string, number> = {};
    for (const [userId, entries] of Object.entries(stagedByUserId)) {
      const sameDayEntries = entries.filter((e) => e.dayOffset === 0);
      if (sameDayEntries.length) {
        counts[userId] = sameDayEntries.length;
        const minutes = sameDayEntries.reduce(
          (sum, { attempt }) => sum + getExpectedDurationMinutes(attempt),
          0
        );
        hours[userId] = minutes / 60;
      }
    }
    return {
      stagedDurationHoursByUserId: hours,
      stagedCountByUserId: counts,
    };
  }, [stagedByUserId]);

  const selectedUserName = useMemo(
    () =>
      assignableUsersAvailableTime.find(
        (u: IAssignableUser) => u.id === selectedUserId
      )?.name,
    [assignableUsersAvailableTime, selectedUserId]
  );

  return (
    <Box className={classes.container}>
      <Typography
        variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
        fontWeight={theme.fontWeight.semiBold}
        color={theme.palette.text.primary}
        noWrap
      >
        Assignable Users:
        {selectedUserName && (
          <Typography
            component="span"
            variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
            fontWeight={theme.fontWeight.semiBold}
            color={theme.palette.primary.main}
          >
            {` Assigning to ${selectedUserName}`}
          </Typography>
        )}
      </Typography>
      {isLoading ? (
        <MiLoader isLoading={isLoading} type={LoaderType.Circular} />
      ) : (
        <Box className={classes.list}>
          {assignableUsersAvailableTime.map((user) => (
            <AssignableUserCard
              key={user.id}
              name={user.name ?? ""}
              availableTime={Math.max(
                0,
                user.availableTime - (stagedDurationHoursByUserId[user.id] ?? 0)
              )}
              assignedWorkOrderCount={
                user.assignedWorkOrdersCount +
                (stagedCountByUserId[user.id] ?? 0)
              }
              stagedWorkOrderCount={stagedByUserId[user.id]?.length ?? 0}
              selected={user.id === selectedUserId}
              showAddButton={!activeAssignableUserIds.includes(user.id)}
              onClick={() => onUserSelect(user.id)}
              onAdd={() => onAddAssignableUser?.(user.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

type AssignableUserContainerProps = {
  assignableUsersAvailableTime: IAssignableUser[];
  isLoading: boolean;
  selectedUserId?: string;
  activeAssignableUserIds?: string[];
  stagedByUserId?: Record<string, StagedWorkOrderAssignment[]>;
  onUserSelect: (userId: string) => void;
  onAddAssignableUser?: (userId: string) => void;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    overflow: "auto",
  },
  list: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: theme.spacing(2),
  },
}));
